import React, { type FC, Fragment } from 'react';

import { AppSidebar } from '@/components/blocks/sidebar/app-sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Separator } from '@/components/ui/separator';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import {
  HeaderActionsProvider,
  useHeaderActionsContainerRef,
} from '@/providers/header-actions-provider';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppHeader: FC = () => {
  const setContainerEl = useHeaderActionsContainerRef();
  const segments = [] as Array<string>;

  return (
    <header className='group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear'>
      <div className='flex flex-1 items-center gap-2 px-4'>
        <SidebarTrigger className='-ml-1' />
        <Separator
          orientation='vertical'
          className='mr-2 data-[orientation=vertical]:h-4'
        />
        <Breadcrumb>
          <BreadcrumbList>
            {segments.map((segment, index) => (
              <Fragment key={segment}>
                <BreadcrumbItem className='hidden md:block'>
                  <BreadcrumbLink href='#'>{segment}</BreadcrumbLink>
                </BreadcrumbItem>
                {index !== segments.length - 1 && (
                  <BreadcrumbSeparator className='hidden md:block' />
                )}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div
        ref={setContainerEl}
        className='ml-auto flex items-center gap-2 px-4'
      />
    </header>
  );
};

const AppLayout: FC<AppLayoutProps> = ({ children }) => (
  <HeaderActionsProvider>
    {/* Viewport-high so `main` is the scroll area and pages can fill it. */}
    <SidebarProvider className='h-svh'>
      <AppSidebar />
      <SidebarInset>
        <AppHeader />
        <main className='m-4 flex min-h-0 flex-1 flex-col gap-4 overflow-auto'>
          {children}
        </main>
      </SidebarInset>
    </SidebarProvider>
  </HeaderActionsProvider>
);

export default AppLayout;
